import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import Store from 'store/store';
import 'store/bolt/geo';
import { accessNested, isEmpty } from 'utils/utils';
// import { closestToCenter } from 'utils/visibilityChecks';
import { getRelativePageUrl, getHeaderHeight, navigateInternally, getMainPage, getPageTitle, getPageDescription } from 'utils/bolt/index';
import { getDeviceInfo } from 'utils/bolt/client';
import { getBaseFilters } from 'utils/bolt/showtimesLoader';
import { geoFromIP } from 'utils/bolt/geo';
import getAppRegions from 'utils/bolt/appRegions';
import geoFencing from 'utils/geoFencing';
import { smoothScrollTo } from 'utils/smoothScroll';
import hooks from 'utils/hooks';
import { sections } from 'pages/bolt_pages';

Store.on('UPDATE_CLIENT', () => {
	const client = Store.get().client;
	const newClient = getDeviceInfo();
	Store.get().set({ client: { ...client, ...newClient } });
});

Store.on('SET_ACTIVE_PAGE', async (newPage, queryString, options) => {
	newPage = await hooks.call('setActivePage.newPage', newPage, { options });

	if (!newPage) {
		return;
	}

	let store = Store.get();
	let activePage = store.activePage;
	let newPageData = store.appData?.pages?.[newPage];

	// Not a fan of this, let's find a better way
	const groupBookingsEnabled = store.appData?.pages?.boltShowtimes?.data?.groupBookings?.isEnabled;
	const isSamePage = newPage === activePage && (newPage !== 'boltShowtimes' || !groupBookingsEnabled);
	if (isSamePage || !newPageData) {
		if (options?.hashInPage && !options?.noUrlUpdate) {
			location.hash = '';
			location.hash = options.hashInPage;
		}
		return;
	}

	// backwards compatibility
	if (queryString === true) queryString = '';
	if (queryString && !queryString.startsWith('?')) {
		queryString = '?' + queryString;
	}
	const passwordPage = newPageData.data?.passwordGatewayPageId;
	if (passwordPage && store.appData.pages[passwordPage]) {
		// Check if password is valid
		try {
			let destination = getRelativePageUrl(newPage, queryString, options?.hashInPage);
			const res = await fetch(destination, { method: 'HEAD' });
			const redirection = res.redirected && new URL(res.url, location.href);
			let passwordPageUrl = new URL(getRelativePageUrl(passwordPage), location.href);
			if (redirection && redirection.origin === passwordPageUrl.origin && redirection.pathname === passwordPageUrl.pathname) {
				location.href = res.url;
				return;
			}
		} catch (e) {}
	}

	let header = Store.get().header;
	if (newPage !== header.activeSection) {
		header.set({ activeSection: newPage });
	}

	if (!options?.noUrlUpdate) {
		let newUrl = getRelativePageUrl(newPage, queryString, options?.hashInPage);
		history.pushState({ activePage: newPage }, '', newUrl);
		document.title = getPageTitle({ store, page: newPage });
		const desc = document.querySelector('meta[name="description"]');
		if (desc && typeof desc === 'object') {
			desc.content = getPageDescription({ store, page: newPage });
		}
	}

	Store.get().set('activePage', newPage);

	if (!newPageData.section && newPage !== 'boltHome' && !window.disableAutoScrollReset) {
		getScroller()?.scrollTo(0, 0);
		Store.emit('SET_SECTION_UNDER_HEADER', null);
	}

	Store.emit('ACTIVE_PAGE_UPDATED', newPage);
	Store.emit('CHECK_ACTIVE_PAGE_AGE_RESTRICTION');
});

Store.on('CHECK_ACTIVE_PAGE_AGE_RESTRICTION', () => {
	let store = Store.get();
	let activePage = store.appData?.pages[store.activePage];
	if (activePage?.ageRestricted && store.client?.ofAge === false) {
		// TODO: improve how to decide which page gets priority
		let destination = [
			'boltHome',
			'boltSynopsis',
			'boltGallery',
			'boltVideos',
			...Object.keys(store.appData?.pages)
		].find(page => {
			let data = store.appData?.pages?.[page];
			return data && !data.ageRestricted;
		});
		Store.emit('CLOSE_AND_NAVIGATE', destination);
	}
});

Store.on('SET_SECTION_UNDER_HEADER', (section) => {
	let header = Store.get().header;
	if (header.sectionUnderHeader !== section) {
		header.set({ sectionUnderHeader: section });
	}
});

function getDesktopOffset(store, id) {
	if (store?.appData?.header?.powsterHeader?.solidBackground) {
		return getHeaderHeight();
	}

	const hasSonyHeader = accessNested(store, 'appData.options.sonyFeatures.sonyHeader', false);
	const isHubPage = accessNested(store, `appData.pages.${id}.component`, '') === 'hub';

	if (hasSonyHeader || isHubPage) {
		return 34;
	} else {
		return 0;
	}
}
function getMobileOffset(store) {
	const hasSonyHeader = accessNested(store, 'appData.options.sonyFeatures.sonyHeader', false);

	if (hasSonyHeader) {
		return 58;
	} else {
		return getHeaderHeight();
	}
}

function getScroller() {
	let mainScroll = document.getElementById('mainScroll');
	if (!mainScroll || getComputedStyle(mainScroll).overflowY === 'visible') {
		return document.scrollingElement;
	}
	return mainScroll;
}

function getSectionElement(id) {
	const store = Store.get();
	const noSlug = store.appData?.pages?.[id];
	if (!noSlug) {
		const pages = accessNested(store, 'appData.pages', null);
		const parentSection = pages && Object.keys(pages).filter(page => pages[page].slug === id)[0];
		id = parentSection || id;
	}
	try {
		return document.querySelector(`[data-section="${id}"]`);
	} catch (e) {
		return null;
	}
}

let sectionLoadVersion = 0;
Store.on('CHANGE_ACTIVE_SECTION', (id, options) => {
	options = options || {};
	if (options.mainPage && !getSectionElement(options.mainPage)) {
		options.instant = false;
	}
	const store = Store.get();

	let toElem = getSectionElement(id);

	const isDesktop = accessNested(store, 'client.isDesktopSize', false);
	const hasUarHeader = accessNested(store, 'appData.header.uarHeader');
	let scrollOffset = isDesktop ? getDesktopOffset(store, id) : getMobileOffset(store);
	if (id === 'boltHome') {
		scrollOffset = 0;
	}
	if (hasUarHeader) {
		scrollOffset = isDesktop ? 80 : 64;
	}

	// for creative projects and whatnot
	const forceScrollOffsetDesktop = store?.appData?.options?.forceScrollOffsetForSection?.[id]?.desktop || store?.appData?.options?.forceScrollOffsetDesktop;
	const forceScrollOffsetMobile = store?.appData?.options?.forceScrollOffsetForSection?.[id]?.mobile || store?.appData?.options?.forceScrollOffsetMobile;
	if (forceScrollOffsetMobile) {
		scrollOffset = +forceScrollOffsetMobile;
	}
	if (isDesktop && forceScrollOffsetDesktop) {
		scrollOffset = +forceScrollOffsetDesktop;
	}

	let ignoreNextScroll = false;
	const go = toElem => {
		if (!toElem) {
			return;
		}
		const scroller = getScroller();
		ignoreNextScroll = true;
		smoothScrollTo(scroller, toElem, options.instant ? 0 : (options.scrollDuration || 400), {
			...options,
			offset: scrollOffset,
			force: true,
			callback: () => Store.emit('CLOSE_ALL_OVERLAYS')
		});
	};

	let version = ++sectionLoadVersion;
	const listener = (loadedId, dom) => {
		if (version !== sectionLoadVersion) {
			setTimeout(() => Store.off('SECTION_LOADED', listener), 0);
			return;
		}
		if (!options.instant) {
			if (loadedId !== id) {
				return;
			}
			setTimeout(() => Store.off('SECTION_LOADED', listener), 0);
		}
		go(loadedId === id ? dom : getSectionElement(id));
	};

	// If section has already loaded scroll to it, otherwise try to load it
	if (toElem) {
		go(toElem);
		Store.off('SECTION_LOADED', listener);
	} else {
		if (id === options.mainPage) {
			getScroller()?.scrollTo(0, 0);
		}
		Store.on('SECTION_LOADED', listener);
		Store.emit('LOAD_SECTION', id);
	}
	if (options.instant) {
		// Remove the automatic scrolling to the desired section once the user has scrolled
		let scrollListener = () => {
			if (ignoreNextScroll) {
				ignoreNextScroll = false;
				return;
			}
			Store.off('SECTION_LOADED', listener);
			window.removeEventListener('scroll', scrollListener, true);
		};
		window.addEventListener('scroll', scrollListener, true);
	}
});

Store.on('POP_STATE', (newPage) => {
	Store.emit('CLOSE_ALL_OVERLAYS', { animate: true });
	// noUrlUpdate since the URL already gets updated from the native browser behaviour
	Store.emit('SET_ACTIVE_PAGE', newPage, undefined, { noUrlUpdate: true });
	updateSiteContent(newPage, { instant: true });
});

let getRegionURL = async countryCode => {
	try {
		let regions = await getAppRegions();
		return regions[countryCode]?.url;
	} catch (e) { }
};

Store.on('LANDING', async ({ page }) => {
	updateSiteContent(page, { instant: true });

	let limitlessKey = false;
	try {
		limitlessKey = localStorage.getItem('limitlessKey');
	} catch (e) { }

	if (limitlessKey) {
		useLimitlessKey();
	}

	const store = Store.get();
	const socialInterstitialEnabled = accessNested(store, 'appData.options.socialInterstitial', false);
	let pageType = store.appData?.pages?.[page]?.type;
	const pageNeedsApi = pageType === 'showtimes' || pageType === 'homeEnt';
	const disableGeo = pageNeedsApi && store.limitExceeded && socialInterstitialEnabled;

	if (!disableGeo) {
		await geoFencing(geoFromIP, { getRegionURL });
	}
});

Store.on('CLOSE_AND_NAVIGATE', (id, e, opts) => {
	if (e && e.preventDefault) {
		e.preventDefault();
	}

	Store.emit('CLOSE_NAV_MENU');

	navigateInternally(id, opts);
});

function updateSiteContent(newPage, options) {
	const page = accessNested(Store.get(), ['appData', 'pages', newPage]);
	if (page?.type === 'showtimes') {
		Store.emit('SHOWTIMES_SHOW', undefined, undefined, newPage);
	} else if (page?.type === 'homeEnt') {
		Store.emit('HOMEENT_SHOW', undefined, undefined, newPage);
	} else if (page?.type === 'pressKit') {
		Store.emit('PRESSKIT_SHOW', undefined, undefined, newPage);
	} else if (page?.type === 'videos') {
		Store.emit('OPEN_VIDEO_OVERLAY', newPage);
	} else {
		const mainScrollPage = getMainPage();
		const isScrollSection = newPage === mainScrollPage || sections.some(p => p.id === newPage && p.parent === mainScrollPage);
		if (isScrollSection) {
			Store.emit('CHANGE_ACTIVE_SECTION', newPage, options);
		}
	}
}

Store.on('LANDING_QUERY', (query = {}) => {
	const store = Store.get();

	if (query.v && store.videos && !store.videos.preSelected) {
		Store.get().videos.set({ preSelected: query.v });
	}

	if (query.debug) {
		Store.get().set({ debugMode: true });
	}
});


Store.on('DISABLE_SCROLLING_LOCK', () => {
	const page = document.getElementById('bolt');
	const app = document.getElementById('app');
	enableBodyScroll(page);
	if (app) {
		app.style.pointerEvents = '';
	}
});

Store.on('ENABLE_SCROLLING_LOCK', (disablePointerEvents) => {
	const page = document.getElementById('bolt');
	const app = document.getElementById('app');
	// disableBodyScroll(page);

	disableBodyScroll(page, {
		allowTouchMove: el => {
			while (el && el !== document.body) {
				if (el.getAttribute('body-scroll-lock-ignore') !== null) {
					return true;
				}

				el = el.parentElement;
			}
		},
	});

	if (disablePointerEvents && app) {
		app.style.pointerEvents = 'none';
	}
});

Store.on('SAFARI_MOBILE_LOCATION_SEARCH_FOCUS', () => {
	document.body.style.height = '5000px';
});

Store.on('SAFARI_MOBILE_LOCATION_SEARCH_BLUR', () => {
	document.body.style.height = '100%';
});

const requestedSections = [];
Store.on('LOAD_SECTION', id => requestedSections.push(id));
Store.on('GET_REQUESTED_SECTIONS', () => requestedSections);

Store.on('OPEN_RESERVED_VARIABLE_LINK', async link => {
	const lines = link?.split('\n') || [];
	for (let line of lines) {
		if (!line?.startsWith('@')) {
			continue;
		}

		let [type, ...args] = line.slice(1).split('::');
		// @date::YYYY-MM-DD as a shortcut for @filter::date::YYYY-MM-DD
		if (['date', 'format', 'time', 'exhibitor'].includes(type.toLowerCase())) {
			args.unshift(type);
			type = 'filter';
		}
		let qs = args[0];

		const store = Store.get();

		switch (type.toLowerCase()) {
			case 'subscriberpopup':
				const mailchimpData = accessNested(store, 'appData.options.formEmbeds.popup');
				return Store.emit('SUBSCRIBER_POPUP', mailchimpData, false);
			case 'calendarreminder':
				return Store.emit('SHOW_CAL_REMIND', args.length ? { date: args[0] } : undefined);
			case 'newsletterbanner':
				return Store.emit('TOGGLE_NEWSLETTER_BANNER', false);
			case 'preferencecentre':
				return Store.emit('COOKIE_PREFERENCES_OPEN');
			case 'otcookiesettings':
				window.OneTrust?.ToggleInfoDisplay();
				break;
			case 'dynamicpriority':
				const hasShowtimesPage = accessNested(store, 'appData.pages.boltShowtimes');
				const dualViewEnabled = accessNested(store, 'appData.options.dualView');
				if (hasShowtimesPage && dualViewEnabled) {
					const hasShowtimes = accessNested(store, 'showtimes.data', []);
					const hasHomeEntPage = accessNested(store, 'appData.pages.boltHomeEnt');
					Store.emit((hasHomeEntPage && isEmpty(hasShowtimes)) ? 'HOMEENT_SHOW' : 'SHOWTIMES_SHOW');
				}
				break;
			case 'openshowtimes':
				Store.emit('SHOWTIMES_SHOW', false, qs);
				let selectedFilters = Store.get().showtimes?.filters?.selected;
				if (qs && selectedFilters) {
					selectedFilters.set(await getBaseFilters(qs));
					if (store.activePage === 'boltShowtimes') {
						Store.emit('SHOWTIMES_REFRESH');
					}
				}
				break;
			case 'filter':
				// ensure the showtimes store functions are loaded (not great)
				// await import(/* webpackChunkName: "showtimes" */ `components/bolt/showtimes/showtimes`);
				// console.log('imported');
				const [filterType, ...filterValues] = args;
				Store.emit('FILTER_SET', filterType, filterValues, filterType !== 'date');
				Store.emit('SHOWTIMES_REFRESH');
				break;
			case 'openhomeent':
				Store.emit('HOMEENT_SHOW', true, qs);
				break;
			// case 'videooverlay':
			// 	const [videoId] = args;
			// 	if (videoId) {
			// 		Store.get().videos.set({ activeVideo: null, preSelected: videoId });
			// 	}
			// 	const pageId = getPageFromVideoId(videoId);
			// 	Store.emit('OPEN_VIDEO_OVERLAY', pageId);
			// 	break;
			case 'videooverlay':
				const trailerOverlay = accessNested(store, 'appData.options.videoOverlay.video', true);
				Store.emit('ENABLE_TRAILER_OVERLAY', trailerOverlay);
		}
	}
});

function useLimitlessKey() {
	const limitlessKey = accessNested(Store.get(), 'appData.meta.powApiLimitlessKey') || 'K4xhuBoeYm59LMIDRkqIz6KKCoIV1Ikq5rLdoe3I';

	Store.get().set({
		apiKey: limitlessKey,
		usingLimitlessKey: true,
		limitExceeded: false
	});
}

Store.on('UPDATE_API_KEY', async () => {
	useLimitlessKey();
	try {
		localStorage.setItem('limitlessKey', true);
	} catch (e) { }
});
